"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type CSSProperties } from "react";
import { Menu, Minus, Plus, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { contactCta, groupCta, navLinks, site, workLinks } from "@/content/site";

const groupButtonCharacters = Array.from(groupCta.label);

function GroupSiteButton({ className = "" }: { className?: string }) {
  return (
    <a
      href={groupCta.href}
      target="_blank"
      rel="noreferrer"
      className={`header-group-action ${className}`}
      aria-label={`${groupCta.label} — opens in a new tab`}
    >
      <span className="header-group-action-text" aria-hidden="true">
        {groupButtonCharacters.map((character, index) => (
          <span
            key={`${character}-${index}`}
            style={{ "--character-index": index } as CSSProperties}
          >
            {character === " " ? "\u00a0" : character}
          </span>
        ))}
      </span>
      <span className="header-group-action-logo" aria-hidden="true">
        <Image
          src="/media/people/Esperer Group Logo.png"
          alt=""
          width={424}
          height={210}
          sizes="112px"
          className="h-[1.8rem] w-auto object-contain"
        />
      </span>
    </a>
  );
}

export function SiteHeader() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [atPageTop, setAtPageTop] = useState(true);
  const solidHeader = !atPageTop || open;

  useEffect(() => {
    const updateHeader = () => {
      setAtPageTop((currentlyAtTop) => {
        if (window.scrollY <= 8) return true;
        if (window.scrollY >= 48) return false;
        return currentlyAtTop;
      });
    };
    const initialFrame = requestAnimationFrame(() => {
      setOpen(false);
      setWorkOpen(false);
      updateHeader();
    });
    window.addEventListener("scroll", updateHeader, { passive: true });

    return () => {
      cancelAnimationFrame(initialFrame);
      window.removeEventListener("scroll", updateHeader);
    };
  }, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,backdrop-filter] duration-200 ${
        solidHeader ? "bg-white/95 backdrop-blur-md" : "bg-transparent backdrop-blur-none"
      }`}
    >
      <div className="mx-auto grid h-[5rem] max-w-[1500px] grid-cols-[1fr_auto] items-center gap-2 px-5 sm:h-[5.5rem] sm:gap-5 sm:px-8 lg:px-12 xl:px-16">
        <div className="flex min-w-0 items-center">
          <Link
            href="/#top"
            className="relative flex shrink-0 items-center overflow-visible"
            onClick={() => setOpen(false)}
            aria-label={`${site.name} home`}
          >
            <Image
              src="/brand/healthvoitho-logo.png"
              alt={site.name}
              width={260}
              height={104}
              preload
              className={`h-[2.8rem] w-auto origin-left object-contain transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] sm:h-[5rem] ${
                solidHeader ? "scale-100" : "scale-[1.08] sm:scale-[1.2]"
              }`}
            />
          </Link>
        </div>

        <div className="flex items-center justify-self-end gap-1.5 sm:gap-2.5">
          <GroupSiteButton className="inline-flex" />
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#7aa2a0] bg-white text-[#315963] shadow-[0_8px_24px_rgba(70,131,151,0.12)] transition hover:-translate-y-0.5 hover:border-[#2f7f80] hover:text-[#2f7f80] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:h-11 sm:w-11"
            aria-expanded={open}
            aria-controls="site-menu"
            onClick={() => {
              setOpen((value) => {
                if (!value) setWorkOpen(false);
                return !value;
              });
            }}
            aria-label={open ? "Close navigation" : "Open navigation"}
          >
            {open ? (
              <X className="h-5 w-5" strokeWidth={1.8} aria-hidden />
            ) : (
              <Menu className="h-5 w-5" strokeWidth={1.8} aria-hidden />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open ? (
          <>
            <motion.button
              type="button"
              aria-label="Close navigation"
              className="absolute inset-x-0 top-0 z-10 h-dvh cursor-default bg-[#071f31]/38 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
            />
            <motion.nav
              id="site-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-0 z-20 h-dvh w-full overflow-y-auto border-l border-[#dfe6e4] bg-white/95 shadow-[-28px_0_70px_rgba(7,31,49,0.18)] backdrop-blur-md sm:max-w-[35rem]"
              aria-label="Primary"
            >
              <div className="flex min-h-full flex-col px-7 pb-9 pt-5 sm:px-11 sm:pb-11 sm:pt-7">
                <div className="flex justify-end pb-4">
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#7aa2a0] bg-white/85 text-[#315963] transition hover:border-[#2f7f80] hover:text-[#2f7f80] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    onClick={() => setOpen(false)}
                    aria-label="Close navigation"
                  >
                    <X className="h-5 w-5" strokeWidth={1.8} aria-hidden />
                  </button>
                </div>
                <div className="flex flex-col border-t border-[#cfdad8]">
                  {navLinks.map((link) => {
                    const active = pathname === link.href;
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`border-b border-[#cfdad8] py-3 font-display text-[clamp(1.55rem,2.8vw,2.15rem)] leading-none tracking-[-0.025em] transition-colors hover:text-[#2f7f80] sm:py-3.5 ${
                          active ? "text-[#2f7f80]" : "text-[#1d315f]"
                        }`}
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </Link>
                    );
                  })}

                  <div className="border-b border-[#cfdad8]">
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between py-3 text-left font-display text-[clamp(1.55rem,2.8vw,2.15rem)] leading-none tracking-[-0.025em] transition-colors hover:text-[#2f7f80] sm:py-3.5 ${
                        workLinks.some((link) => pathname === link.href)
                          ? "text-[#2f7f80]"
                          : "text-[#1d315f]"
                      }`}
                      aria-expanded={workOpen}
                      aria-controls="work-menu"
                      onClick={() => setWorkOpen((value) => !value)}
                    >
                      Work
                      {workOpen ? (
                        <Minus className="h-6 w-6 shrink-0" strokeWidth={1.5} aria-hidden />
                      ) : (
                        <Plus className="h-6 w-6 shrink-0" strokeWidth={1.5} aria-hidden />
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {workOpen ? (
                        <motion.div
                          id="work-menu"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="ml-5 space-y-4 pb-5 pt-2 sm:ml-7">
                            {workLinks.map((link) => (
                              <Link
                                key={link.href}
                                href={link.href}
                                className={`group grid grid-cols-[5.75rem_minmax(0,1fr)] items-center gap-4 transition duration-300 sm:grid-cols-[7rem_minmax(0,1fr)] ${
                                  pathname === link.href
                                    ? "text-[#2f7f80]"
                                    : ""
                                }`}
                                onClick={() => setOpen(false)}
                              >
                                <span className="relative block aspect-[16/10] overflow-hidden rounded-md border border-[#d5e0dd] bg-[#f6f8f7] transition-shadow duration-300 group-hover:shadow-[0_8px_20px_rgba(16,46,58,0.1)]">
                                  <Image
                                    src={link.image}
                                    alt={link.imageAlt}
                                    fill
                                    sizes="(max-width: 639px) 92px, 112px"
                                    className={`transition-transform duration-500 group-hover:scale-[1.025] ${link.imageClassName}`}
                                  />
                                </span>
                                <span className="block text-sm font-semibold text-[#315963] transition-colors group-hover:text-[#2f7f80]">
                                  {link.label}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>

                  <Link
                    href={contactCta.href}
                    className={`py-3 font-display text-[clamp(1.55rem,2.8vw,2.15rem)] leading-none tracking-[-0.025em] transition-colors hover:text-[#2f7f80] sm:py-3.5 ${
                      pathname === contactCta.href ? "text-[#2f7f80]" : "text-[#1d315f]"
                    }`}
                    onClick={() => setOpen(false)}
                  >
                    {contactCta.label}
                  </Link>
                </div>
              </div>
            </motion.nav>
          </>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
